import { useState, useRef } from 'react';
import cx from 'classnames';
import { useMediaQuery, ClickAwayListener, IconButton, Badge } from '@mui/material';
import Fade from '@mui/material/Fade';
import { makeStyles } from '@mui/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Popper from '@mui/material/Popper';

import { getPathName, ROUTES } from '@openclassrooms/routing';
import { translate } from '@openclassrooms/translations';

import { useTheme, alpha } from '@classify/react-core-components/styles';
import Box from '@classify/react-core-components/Box';
import browser from '@openclassrooms/utils/browser';

import MainHeaderMenu from '../MainHeaderMenu';

import MemberAvatar from './MemberAvatar';
import MainHeaderVisitorMenu from './MainHeaderVisitorMenu';
import VisitorAvatar from './VisitorAvatar';

const focusDelay = 300;
const mainHeaderMenuId = 'main-header-menu';

const useStyles = makeStyles(
  ({ spacing, palette, transitions }) => ({
    avatarButton: {
      border: `1px solid ${palette.layout.line.divider}`,
      borderRadius: 50,
      padding: spacing(0.5),
      '&:hover,&:focus': {
        backgroundColor: alpha(palette.brand.main, 0.16),
      },
    },
    avatarButtonIcon: {
      color: palette.text.onMain,
      marginLeft: spacing(1),
      marginRight: spacing(0.5),
      transition: `transform ${transitions.duration.shortest}ms ${transitions.easing.easeInOut}`,
    },
    avatarButtonExpanded: {
      transform: 'rotate(180deg)',
    },
    avatarWrapper: {
      paddingBottom: spacing(3),
      marginBottom: spacing(-3),
      marginLeft: spacing(1),
      '.jobSurvey &': {
        display: 'none',
      },
    },
    menuWrapper: {
      margin: [[spacing(-2), 0, spacing(-2), 0]],
      zIndex: 1301,
    },
    avatarBadge: {
      backgroundColor: palette.primary.main,
      top: 7,
      right: 7,
      border: `1px solid ${palette.text.onContrasted}`,
    },
  }),
  { classNamePrefix: 'MainHeaderActions' }
);

const MainHeaderActions = ({ user = {}, right = {}, onLogOut = () => {} }) => {
  const anchorRef = useRef(null);
  const [isMenuOpen, toggleMenu] = useState(false);
  const classes = useStyles();

  const { breakpoints } = useTheme();
  const shouldHaveAvatarMenuFullWidth = useMediaQuery(breakpoints.down('sm'));
  const shouldRenderTinyAvatar = useMediaQuery(breakpoints.down('md'));
  const toggleMenuTimeout = useMediaQuery(breakpoints.down('lg')) ? 400 : 0;

  const profileAvatar = user ? (
    <MemberAvatar user={user} isSmaller={shouldRenderTinyAvatar} />
  ) : (
    <VisitorAvatar isSmaller={shouldRenderTinyAvatar} />
  );

  const profileAvatarWithBadge =
    user && user.notificationCount + user.messageCount + user.moderationNotificationCount > 0 ? (
      <Badge
        classes={{ colorPrimary: classes.avatarBadge }}
        color="primary"
        badgeContent={user.notificationCount + user.messageCount + user.moderationNotificationCount}
      >
        {profileAvatar}
      </Badge>
    ) : (
      profileAvatar
    );

  const buttonProfileAvatar = (
    <IconButton
      data-event={user ? undefined : 'Button|Clicked|header-signin'}
      className={classes.avatarButton}
      disableFocusRipple
      aria-owns={isMenuOpen ? mainHeaderMenuId : null}
      aria-haspopup
      aria-expanded={isMenuOpen}
      title="Menu"
      onClick={() => {
        toggleMenu(!isMenuOpen);
        browser.document.activeElement.blur();
        setTimeout(() => browser.document.getElementById(mainHeaderMenuId).focus(), focusDelay);
      }}
      data-testid="mainHeaderAvatar"
      aria-label={translate('global.word.menu')}
    >
      <div>{profileAvatarWithBadge}</div>
      <ExpandMore
        className={cx(classes.avatarButtonIcon, {
          [classes.avatarButtonExpanded]: isMenuOpen,
        })}
      />
    </IconButton>
  );

  const popperSx = {
    zIndex: 'appBar',
  };

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          setTimeout(() => toggleMenu(false), toggleMenuTimeout);
        }}
      >
        <Box ref={anchorRef} sx={{ marginLeft: 2 }}>
          {buttonProfileAvatar}
        </Box>
      </ClickAwayListener>

      <Popper
        open={isMenuOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        {...(shouldHaveAvatarMenuFullWidth
          ? {
              modifiers: [{ name: 'offset', options: { offset: [16, 0] } }],
              sx: { ...popperSx, width: '100%' },
            }
          : { sx: popperSx })}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <div>
              {user ? (
                <MainHeaderMenu
                  id={mainHeaderMenuId}
                  messageCount={user.messageCount}
                  displayableName={user.displayableName}
                  profileAvatar={profileAvatar}
                  notificationCount={user.notificationCount}
                  moderationNotificationCount={user.moderationNotificationCount}
                  canSeeMentorDashboard={right.canSeeMentorDashboard}
                  canSeeOrganizationDashboard={right.canSeeOrganizationDashboard}
                  canSeeAdminDashboard={right.canSeeAdminDashboard}
                  canSeeTutorDashboard={right.canSeeTutorDashboard}
                  canDisplayForumAlert={right.canDisplayForumAlert}
                  profileLink={getPathName(ROUTES.ACCOUNT.INDEX)}
                  profileCompletionRate={user.profileCompletionRate}
                  organizationName={user.organizationName}
                  employerSpaceLink={user.link.employerSpace}
                  onLogOut={onLogOut}
                />
              ) : (
                <MainHeaderVisitorMenu />
              )}
            </div>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default MainHeaderActions;
